import { jsx, jsxs } from "react/jsx-runtime";
import { Dialog } from "../dialog/index.js";
import { SegmentedControl } from "../segmented-control/index.js";
import { useGraphiQL, useGraphiQLActions } from "../provider.js";
import { useGraphiQLSettings } from "../../hooks/use-graphiql-settings.js";
import { useState, useEffect } from "react";
/* empty css          */
import { useMonaco } from "../../stores/monaco.js";
const CLEAR_STORAGE_CONFIRMATION_MS = 1500;
const CheckIcon = () => /* @__PURE__ */ jsx("svg", { "aria-hidden": "true", viewBox: "0 0 16 16", fill: "none", className: "graphiql-settings-check-icon", children: /* @__PURE__ */ jsx("path", { stroke: "currentColor", strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "1.75", d: "m3.5 8.5 3 3 6-7" }) });
const FONT_SIZE_PX = {
  compact: 12,
  default: 13,
  large: 14,
  xl: 16
};
const FORCED_THEME_TO_SETTING = {
  light: "light",
  dark: "dark",
  system: "auto"
};
const PERSIST_HEADERS_OPTIONS = [{
  value: "on",
  label: "On"
}, {
  value: "off",
  label: "Off"
}];
const THEME_OPTIONS = [{
  value: "auto",
  label: "Auto"
}, {
  value: "light",
  label: "Light"
}, {
  value: "dark",
  label: "Dark"
}];
const DENSITY_OPTIONS = [{
  value: "compact",
  label: "Compact"
}, {
  value: "comfortable",
  label: "Comfortable"
}, {
  value: "spacious",
  label: "Spacious"
}];
const FONT_SIZE_OPTIONS = [{
  value: "compact",
  label: "Compact"
}, {
  value: "default",
  label: "Default"
}, {
  value: "large",
  label: "Large"
}, {
  value: "xl",
  label: "Extra Large"
}];
const SettingsDialog = ({
  open,
  onOpenChange,
  forcedTheme,
  showPersistHeadersSettings,
  restoreFocusRef
}) => {
  const {
    theme,
    setTheme,
    density,
    setDensity,
    fontSize,
    setFontSize
  } = useGraphiQLSettings();
  const monaco = useMonaco((state) => state.monaco);
  const shouldPersistHeaders = useGraphiQL((state_0) => state_0.shouldPersistHeaders);
  const storage = useGraphiQL((state_1) => state_1.storage);
  const {
    setShouldPersistHeaders
  } = useGraphiQLActions();
  const [isDataCleared, setIsDataCleared] = useState(false);
  useEffect(() => {
    if (!open) {
      setIsDataCleared(false);
    }
  }, [open]);
  useEffect(() => {
    if (!isDataCleared) {
      return;
    }
    const timer = setTimeout(() => setIsDataCleared(false), CLEAR_STORAGE_CONFIRMATION_MS);
    return () => clearTimeout(timer);
  }, [isDataCleared]);
  function handleClearData() {
    storage.clear();
    setIsDataCleared(true);
  }
  useEffect(() => {
    if (!monaco) {
      return;
    }
    const px = FONT_SIZE_PX[fontSize];
    monaco.editor.getEditors().forEach((editor) => {
      editor.updateOptions({
        fontSize: px
      });
    });
  }, [fontSize, monaco]);
  const forcedThemeSetting = forcedTheme ? FORCED_THEME_TO_SETTING[forcedTheme] : void 0;
  useEffect(() => {
    if (forcedThemeSetting && theme !== forcedThemeSetting) {
      setTheme(forcedThemeSetting);
    }
  }, [forcedThemeSetting, theme, setTheme]);
  return /* @__PURE__ */ jsx(Dialog, { open, onOpenChange, restoreFocusRef, children: /* @__PURE__ */ jsxs("div", { className: "graphiql-settings-dialog", children: [
    /* @__PURE__ */ jsx(Dialog.Header, { children: "Settings" }),
    /* @__PURE__ */ jsxs("div", { className: "graphiql-settings-dialog-body", children: [
      !forcedThemeSetting && /* @__PURE__ */ jsxs("section", { className: "graphiql-settings-section", children: [
        /* @__PURE__ */ jsx("h3", { className: "graphiql-settings-section-title", children: "Theme" }),
        /* @__PURE__ */ jsx(SegmentedControl, { value: theme, onChange: setTheme, ariaLabel: "Theme", options: THEME_OPTIONS })
      ] }),
      /* @__PURE__ */ jsxs("section", { className: "graphiql-settings-section", children: [
        /* @__PURE__ */ jsx("h3", { className: "graphiql-settings-section-title", children: "Density" }),
        /* @__PURE__ */ jsx(SegmentedControl, { value: density, onChange: setDensity, ariaLabel: "Density", options: DENSITY_OPTIONS })
      ] }),
      /* @__PURE__ */ jsxs("section", { className: "graphiql-settings-section", children: [
        /* @__PURE__ */ jsx("h3", { className: "graphiql-settings-section-title", children: "Font size" }),
        /* @__PURE__ */ jsx(SegmentedControl, { value: fontSize, onChange: setFontSize, ariaLabel: "Font size", options: FONT_SIZE_OPTIONS })
      ] }),
      showPersistHeadersSettings && /* @__PURE__ */ jsxs("section", { className: "graphiql-settings-section", children: [
        /* @__PURE__ */ jsxs("div", { className: "graphiql-settings-section-label", children: [
          /* @__PURE__ */ jsx("h3", { className: "graphiql-settings-section-title", children: "Persist headers" }),
          /* @__PURE__ */ jsx("p", { className: "graphiql-settings-section-caption", children: "Save headers upon reloading. Only enable if you trust this device." })
        ] }),
        /* @__PURE__ */ jsx(SegmentedControl, { value: shouldPersistHeaders ? "on" : "off", onChange: (value) => setShouldPersistHeaders(value === "on"), ariaLabel: "Persist headers", options: PERSIST_HEADERS_OPTIONS })
      ] }),
      /* @__PURE__ */ jsxs("section", { className: "graphiql-settings-section", children: [
        /* @__PURE__ */ jsxs("div", { className: "graphiql-settings-section-label", children: [
          /* @__PURE__ */ jsx("h3", { className: "graphiql-settings-section-title", children: "Clear storage" }),
          /* @__PURE__ */ jsx("p", { className: "graphiql-settings-section-caption", children: "Remove all locally stored data and start fresh." })
        ] }),
        /* @__PURE__ */ jsxs("button", { type: "button", className: "graphiql-settings-clear-button", onClick: handleClearData, "data-confirmed": isDataCleared || void 0, children: [
          /* @__PURE__ */ jsx("span", { className: "graphiql-settings-clear-button-label", children: "Clear data" }),
          /* @__PURE__ */ jsx("span", { className: "graphiql-settings-clear-button-check", "aria-hidden": "true", children: /* @__PURE__ */ jsx(CheckIcon, {}) })
        ] }),
        /* @__PURE__ */ jsx("span", { className: "graphiql-settings-sr-only", role: "status", children: isDataCleared ? "Data cleared" : "" })
      ] })
    ] })
  ] }) });
};
export {
  SettingsDialog
};
//# sourceMappingURL=index.js.map
